import type {
  MarketingCampaign,
  MarketingCampaignBudget,
  MarketingCampaignBudgetScheduled,
  MarketingCampaignBudgetScheduleWindow,
  MarketingCampaignBudgetSegment,
  MarketingCampaignBudgetSimple,
} from "../interfaces/KYDMarketing";
import { minorAmountToPrice } from "./minorAmountToPrice";

/** Meta platform upper bound for phased schedule rows (reference; KYD caps lower). */
export const META_BUDGET_SCHEDULE_MAX_SEGMENTS = 50;

/** KYD product cap on scheduled budget phases (segments); API + UI + timeline validation. */
export const SCHEDULED_BUDGET_MAX_SEGMENTS = 10;

/**
 * Meta rejects `budget_schedules` times that are not on a 15-minute grid (UTC):
 * minute must be 0, 15, 30, or 45 (error_subcode 3858205).
 */
export const META_BUDGET_SCHEDULE_INTERVAL_SEC = 15 * 60;

/**
 * Product rule (KYD): each phased campaign budget segment must span at least this wall-clock
 * duration between `time_start` and `time_end` (ISO instants). Enforced in
 * {@link validateBudgetSegments}, UI timeline checks, and create/edit APIs.
 * Max segment count uses {@link SCHEDULED_BUDGET_MAX_SEGMENTS}.
 */
export const SCHEDULED_BUDGET_PHASE_MIN_DURATION_HOURS = 3;
export const SCHEDULED_BUDGET_PHASE_MIN_DURATION_SECONDS =
  SCHEDULED_BUDGET_PHASE_MIN_DURATION_HOURS * 60 * 60;

export const SCHEDULED_BUDGET_PHASE_MIN_DURATION_MESSAGE = `Each budget phase must span at least ${SCHEDULED_BUDGET_PHASE_MIN_DURATION_HOURS} hours.`;

/**
 * Snap a Unix timestamp (seconds, UTC) to Meta's required 15-minute boundary.
 */
export function snapUnixSecondsToMetaBudgetScheduleUtc(
  unixSeconds: number,
  mode: "floor" | "ceil"
): number {
  const q = META_BUDGET_SCHEDULE_INTERVAL_SEC;
  return mode === "floor"
    ? Math.floor(unixSeconds / q) * q
    : Math.ceil(unixSeconds / q) * q;
}

/**
 * Align **campaign run** flight instants (UTC ISO) to Meta’s 15-minute UTC grid before comparing
 * phased budget segments, which are authored on that same grid (see guided
 * `snapWallMinuteToMetaBudgetUtc` / {@link initialTwoScheduledPhaseWallRows}).
 *
 * Without this, a run start like `13:07Z` fails validation against a first phase floored to
 * `13:00Z` (“phases must not start before the ad run schedule”) even though the UI is consistent.
 */
export function snapUtcFlightIsoBoundsToMetaBudgetScheduleGrid(params: {
  flightStartIso: string;
  flightEndIso: string;
}): { flightStartIso: string; flightEndIso: string } {
  const startMs = Date.parse(params.flightStartIso.trim());
  const endMs = Date.parse(params.flightEndIso.trim());
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) {
    return {
      flightStartIso: params.flightStartIso.trim(),
      flightEndIso: params.flightEndIso.trim(),
    };
  }
  const q = META_BUDGET_SCHEDULE_INTERVAL_SEC;
  const startSec = snapUnixSecondsToMetaBudgetScheduleUtc(
    Math.floor(startMs / 1000),
    "floor"
  );
  let endSec = snapUnixSecondsToMetaBudgetScheduleUtc(
    Math.floor(endMs / 1000),
    "ceil"
  );
  if (endSec <= startSec) {
    endSec = startSec + q;
  }
  return {
    flightStartIso: new Date(startSec * 1000).toISOString(),
    flightEndIso: new Date(endSec * 1000).toISOString(),
  };
}

/**
 * Snap a single UTC ISO instant to Meta’s 15m grid — **floor** for phase starts (matches guided
 * `snapWallMinuteToMetaBudgetUtc` `start`) and **ceil** for phase ends (`end`).
 */
export function snapUtcIsoInstantToMetaBudgetScheduleGrid(
  iso: string,
  bound: "start" | "end"
): string {
  const trimmed = iso.trim();
  const ms = Date.parse(trimmed);
  if (!Number.isFinite(ms)) {
    return trimmed;
  }
  const sec = snapUnixSecondsToMetaBudgetScheduleUtc(
    Math.floor(ms / 1000),
    bound === "start" ? "floor" : "ceil"
  );
  return new Date(sec * 1000).toISOString();
}

/**
 * Snap a schedule window so both ends satisfy Meta's 15-minute rule while keeping
 * `time_end > time_start` (expands the window slightly when needed).
 *
 * **`time_start` uses `ceil`:** Meta allows floor or ceil as long as times sit on the 15m grid.
 * Using `floor` on starts pulled boosted windows **earlier** than the authored segment start
 * (e.g. `08:50Z` → `08:45Z`), which extends high-demand into the prior phase for contiguous
 * segments. **`ceil`** snaps the start forward so the Meta window does not begin before the
 * segment’s ISO `time_start` (same example → `09:00Z`).
 */
export function snapMetaBudgetScheduleWindowUnixRange(params: {
  time_start: number;
  time_end: number;
}): { time_start: number; time_end: number } {
  const q = META_BUDGET_SCHEDULE_INTERVAL_SEC;
  const time_start = snapUnixSecondsToMetaBudgetScheduleUtc(
    params.time_start,
    "floor"
  );
  let time_end = snapUnixSecondsToMetaBudgetScheduleUtc(
    params.time_end,
    "ceil"
  );
  if (time_end <= time_start) {
    time_end = time_start + q;
  }
  return { time_start, time_end };
}

/**
 * Per-window 15m snap uses **`floor` on starts** (see {@link snapMetaBudgetScheduleWindowUnixRange})
 * and **`ceil` on ends**. Adjacent boosted windows can still overlap after snap if boundaries
 * are awkward; this routine sorts and shifts later starts to the previous row’s `time_end`.
 *
 * Meta rejects overlapping high-demand periods (`error_subcode` 3858167).
 */
export function normalizeSnappedMetaBudgetScheduleWindowsNonOverlapping<
  T extends { time_start: number; time_end: number },
>(rows: T[]): T[] {
  if (rows.length <= 1) return rows;
  const q = META_BUDGET_SCHEDULE_INTERVAL_SEC;
  const sorted = [...rows].sort((a, b) =>
    a.time_start !== b.time_start
      ? a.time_start - b.time_start
      : a.time_end - b.time_end
  );
  const out = sorted.map((r) => ({ ...r }));
  for (let i = 1; i < out.length; i++) {
    if (out[i].time_start < out[i - 1].time_end) {
      out[i].time_start = out[i - 1].time_end;
    }
    if (out[i].time_end <= out[i].time_start) {
      out[i].time_end = out[i].time_start + q;
    }
  }
  return out;
}

/** @deprecated Use {@link META_BUDGET_SCHEDULE_MAX_SEGMENTS}. */
export const META_BUDGET_SCHEDULE_MAX_WINDOWS =
  META_BUDGET_SCHEDULE_MAX_SEGMENTS;

export type CampaignBudgetWindowsValidationErrorCode =
  | "NO_SEGMENTS"
  | "NO_WINDOWS"
  | "WINDOW_INVALID_RANGE"
  | "TOO_MANY_SEGMENTS"
  | "TOO_MANY_WINDOWS"
  | "INVALID_ISO_TIME"
  | "TIMELINE_GAP"
  | "BOUNDARY_INVALID"
  | "OUTSIDE_FLIGHT_START"
  | "OUTSIDE_FLIGHT_END"
  | "FLIGHT_START_MISMATCH"
  | "FLIGHT_END_MISMATCH"
  | "PHASE_TOO_SHORT";

export type CampaignBudgetWindowsValidationError = {
  code: CampaignBudgetWindowsValidationErrorCode;
  message: string;
};

/** Base slice for Meta when the campaign uses a simple daily/lifetime budget. */
export function metaBaseBudgetFromSimpleCampaignBudget(
  budget: MarketingCampaignBudgetSimple
): MarketingCampaignBudgetSimple {
  return budget;
}

/**
 * Derives Meta base (min segment **daily-equivalent**) + optional `budget_schedules` rows from phased segments.
 *
 * For each segment, KYD treats `amount` as total phase spend and computes:
 * `daily_equivalent = segment_amount / segment_duration_days`.
 *
 * Meta campaign `daily_budget` is the **minimum daily-equivalent** across segments. For each boosted
 * segment, Graph `budget_schedules` with `budget_value_type: ABSOLUTE` expects the **increment**
 * above that base daily budget (not the segment total): effective cap for the window ≈
 * `daily_budget + budget_value`.
 */
export function phasedSegmentsToMetaBaseAndScheduleRows(
  budget: MarketingCampaignBudgetScheduled
): {
  base: MarketingCampaignBudgetSimple;
  scheduleRows: MarketingCampaignBudgetScheduleWindow[];
} {
  const { segments } = budget;
  if (!segments.length) {
    throw new Error("phasedSegmentsToMetaBaseAndScheduleRows: empty segments");
  }

  const segmentDailyMinorRates: Array<{
    time_start: string;
    time_end: string;
    dailyAmount: number;
  }> = [];
  let minDailyMinor = Infinity;
  let minCurrency = segments[0].amount.currency ?? "USD";

  for (const s of segments) {
    const amt = coerceFiniteMinorAmount(s.amount?.amount);
    if (amt == null || amt <= 0) continue;
    const startMs = Date.parse(s.time_start);
    const endMs = Date.parse(s.time_end);
    const durationDays = (endMs - startMs) / 86_400_000;
    if (!Number.isFinite(durationDays) || durationDays <= 0) {
      continue;
    }
    const dailyAmount = amt / durationDays;
    if (!Number.isFinite(dailyAmount) || dailyAmount <= 0) {
      continue;
    }
    segmentDailyMinorRates.push({
      time_start: s.time_start,
      time_end: s.time_end,
      dailyAmount: dailyAmount,
    });
    if (dailyAmount < minDailyMinor) {
      minDailyMinor = dailyAmount;
      minCurrency = s.amount.currency ?? minCurrency;
    }
  }

  if (!Number.isFinite(minDailyMinor) || minDailyMinor <= 0) {
    throw new Error(
      "phasedSegmentsToMetaBaseAndScheduleRows: could not resolve min segment daily amount"
    );
  }
  const baseMinorRounded = Math.max(1, Math.floor(minDailyMinor));
  const base: MarketingCampaignBudgetSimple = {
    type: "daily",
    amount: minorAmountToPrice(baseMinorRounded, minCurrency),
  };
  const scheduleRows: MarketingCampaignBudgetScheduleWindow[] = [];
  for (const seg of segmentDailyMinorRates) {
    const segmentMinorRounded = Math.max(1, Math.floor(seg.dailyAmount));
    const incrementMinor = segmentMinorRounded - baseMinorRounded;
    if (incrementMinor <= 0) continue;
    scheduleRows.push({
      time_start: seg.time_start,
      time_end: seg.time_end,
      budget_value: incrementMinor,
      budget_value_type: "ABSOLUTE",
    });
  }
  return { base, scheduleRows };
}

/**
 * Graph `daily_budget` / `lifetime_budget` for campaign create/update, plus derived high-demand
 * windows when applicable (POST each window to `/{campaign_id}/budget_schedules` after create/update).
 *
 * Meta requires **daily_budget** (not `lifetime_budget`) when the campaign uses high-demand
 * budget scheduling (OAuth `error_subcode` 3858199 pairs daily budget with the `budget_schedules` edge).
 * Scheduled budgets with at least one boosted row therefore use `daily_budget` only
 * (minimum daily-equivalent across segments); scheduled budgets with no boosted rows also use
 * `daily_budget` since the derived base is a per-day amount.
 * Each `scheduleRows[].budget_value` with `ABSOLUTE` is the **delta** above that `daily_budget` for the window.
 */
export function metaCampaignBudgetWireForGraph(
  budget: MarketingCampaignBudget | undefined
): {
  daily_budget?: number;
  lifetime_budget?: number;
  scheduleRows: MarketingCampaignBudgetScheduleWindow[];
} {
  if (!budget) {
    return { scheduleRows: [] };
  }
  if (budget.type === "scheduled" && (budget.segments ?? []).length > 0) {
    const derived = phasedSegmentsToMetaBaseAndScheduleRows(budget);
    const amt = derived.base.amount?.amount;
    const n = typeof amt === "number" && Number.isFinite(amt) ? amt : undefined;
    return {
      ...(n != null ? { daily_budget: n } : {}),
      scheduleRows: derived.scheduleRows,
    };
  }
  if (budget.type === "daily") {
    const amt = budget.amount?.amount;
    const n = typeof amt === "number" && Number.isFinite(amt) ? amt : undefined;
    return { ...(n != null ? { daily_budget: n } : {}), scheduleRows: [] };
  }
  if (budget.type === "lifetime") {
    const amt = budget.amount?.amount;
    const n = typeof amt === "number" && Number.isFinite(amt) ? amt : undefined;
    return { ...(n != null ? { lifetime_budget: n } : {}), scheduleRows: [] };
  }
  return { scheduleRows: [] };
}

/** Base slice for Meta when the campaign uses phased scheduled budget (min daily-equivalent segment). */
export function metaBaseBudgetFromScheduledCampaignBudget(
  budget: MarketingCampaignBudgetScheduled
): MarketingCampaignBudgetSimple {
  return phasedSegmentsToMetaBaseAndScheduleRows(budget).base;
}

/**
 * Numbers derived from a KYD campaign budget for Meta’s **campaign** node (`daily_budget` /
 * `lifetime_budget`, minor units) plus optional **high-demand** windows (`scheduleRows`) that are
 * sent in a second step to `POST /{campaign_id}/budget_schedules`.
 *
 * This is what {@link metaCampaignBudgetWireForGraph} computes; the handle exposes the same shape
 * as {@link CampaignBudgetHandle.metaGraphBudgetFields}.
 */
export type MetaCampaignGraphBudgetFields = ReturnType<
  typeof metaCampaignBudgetWireForGraph
>;

/** @deprecated Renamed to {@link MetaCampaignGraphBudgetFields}. */
export type MetaCampaignBudgetWire = MetaCampaignGraphBudgetFields;

/**
 * How the budget is stored in KYD (not Meta’s vocabulary):
 *
 * - **`none`**: no `campaign.budget` (or empty handling).
 * - **`simple`**: a normal **daily or lifetime** budget — one `amount` on the campaign. In KYD
 *   this is `MarketingCampaignBudget` with `type: "daily" | "lifetime"`.
 * - **`scheduled`**: a **phased** budget — many time-bounded **segments**, each with its own amount.
 *   In KYD this is `type: "scheduled"`. Meta still needs one “base” daily/lifetime pair on the
 *   campaign (we use the minimum segment) plus separate rows for boosted/high-demand windows.
 *
 * Most callers should **not** branch on `tag`: use {@link CampaignBudgetHandle.metaGraphBudgetFields},
 * {@link CampaignBudgetHandle.baseForMeta}, {@link CampaignBudgetHandle.displayMinorTotal}, and
 * the `needs*` helpers. Use {@link CampaignBudgetHandle.fold} only when you must produce different
 * **KYD-shaped** objects (e.g. scaling every segment vs scaling one amount).
 */
export type CampaignBudgetTag = "none" | "simple" | "scheduled";

/**
 * Single entry point for campaign budget behavior (simple vs scheduled vs missing).
 * Prefer this over scattered `budget.type === "scheduled"` checks at call sites.
 */
export interface CampaignBudgetHandle {
  readonly tag: CampaignBudgetTag;

  /**
   * Budget numbers Meta expects on the **campaign** create/update form, plus any derived
   * high-demand windows for the `budget_schedules` edge. Not a network call — pure mapping from KYD
   * → Meta field names.
   */
  metaGraphBudgetFields(): MetaCampaignGraphBudgetFields;

  /** True when boosted schedule rows exist and must be POSTed to `/{id}/budget_schedules`. */
  needsMetaBudgetScheduleEdge(): boolean;

  /** True when campaign create/update should send `LOWEST_COST_WITHOUT_CAP` for budget fields. */
  needsLowestCostWithoutCapBidStrategy(): boolean;

  /** Min-segment base for Meta `daily_budget` / `lifetime_budget` (scheduled) or the simple budget. */
  baseForMeta(): MarketingCampaignBudgetSimple | undefined;

  /** Sum of segment minors (scheduled) or single amount (simple); for UI / aggregates. */
  displayMinorTotal(): number | null;

  isCompleteForMetaPublish(): boolean;

  /** Meta `budget_schedules` Graph ids on boosted segments (`external_id`). */
  storedMetaScheduleGraphIds(): string[];

  fold<R>(arms: {
    none: () => R;
    simple: (b: MarketingCampaignBudgetSimple) => R;
    scheduled: (b: MarketingCampaignBudgetScheduled) => R;
  }): R;
}

function storedMetaScheduleGraphIdsFromScheduled(
  budget: MarketingCampaignBudgetScheduled
): string[] {
  const out: string[] = [];
  for (const seg of budget.segments ?? []) {
    const sid = seg.external_id?.trim();
    if (sid) out.push(sid);
  }
  return [...new Set(out)];
}

/** Minor-unit amount from API/JSON that may use number or numeric string. */
function coerceFiniteMinorAmount(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const t = value.trim();
    if (t === "") return null;
    const n = Number(t);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function displayMinorTotalScheduled(
  budget: MarketingCampaignBudgetScheduled
): number | null {
  const segs = budget.segments ?? [];
  if (segs.length === 0) return null;
  let sumMinor = 0;
  let sawValid = false;
  for (const s of segs) {
    const a = coerceFiniteMinorAmount(s.amount?.amount);
    if (a != null && a >= 0) {
      sumMinor += a;
      sawValid = true;
    }
  }
  return sawValid ? sumMinor : null;
}

function isCompleteScheduledForMetaPublish(
  budget: MarketingCampaignBudgetScheduled
): boolean {
  const segs = budget.segments ?? [];
  if (segs.length === 0) return false;
  return segs.every(
    (s) => typeof s.amount?.amount === "number" && s.amount.amount > 0
  );
}

export function campaignBudgetHandle(
  budget: MarketingCampaignBudget | undefined
): CampaignBudgetHandle {
  if (!budget) {
    return {
      tag: "none",
      metaGraphBudgetFields: () => ({ scheduleRows: [] }),
      needsMetaBudgetScheduleEdge: () => false,
      needsLowestCostWithoutCapBidStrategy: () => false,
      baseForMeta: () => undefined,
      displayMinorTotal: () => null,
      isCompleteForMetaPublish: () => false,
      storedMetaScheduleGraphIds: () => [],
      fold: (arms) => arms.none(),
    };
  }

  if (budget.type === "scheduled") {
    const b = budget;
    let memo: MetaCampaignGraphBudgetFields | undefined;
    const getMetaGraphBudgetFields = (): MetaCampaignGraphBudgetFields =>
      (memo ??= metaCampaignBudgetWireForGraph(b));

    return {
      tag: "scheduled",
      metaGraphBudgetFields: () => getMetaGraphBudgetFields(),
      needsMetaBudgetScheduleEdge: () =>
        getMetaGraphBudgetFields().scheduleRows.length > 0,
      needsLowestCostWithoutCapBidStrategy: () => {
        const w = getMetaGraphBudgetFields();
        return (
          w.daily_budget != null ||
          w.lifetime_budget != null ||
          w.scheduleRows.length > 0
        );
      },
      baseForMeta: () => metaBaseBudgetFromScheduledCampaignBudget(b),
      displayMinorTotal: () => displayMinorTotalScheduled(b),
      isCompleteForMetaPublish: () => isCompleteScheduledForMetaPublish(b),
      storedMetaScheduleGraphIds: () =>
        storedMetaScheduleGraphIdsFromScheduled(b),
      fold: (arms) => arms.scheduled(b),
    };
  }

  const b = budget;
  return {
    tag: "simple",
    metaGraphBudgetFields: () => metaCampaignBudgetWireForGraph(b),
    needsMetaBudgetScheduleEdge: () => false,
    needsLowestCostWithoutCapBidStrategy: () => {
      const w = metaCampaignBudgetWireForGraph(b);
      return w.daily_budget != null || w.lifetime_budget != null;
    },
    baseForMeta: () => metaBaseBudgetFromSimpleCampaignBudget(b),
    displayMinorTotal: () => coerceFiniteMinorAmount(b.amount?.amount),
    isCompleteForMetaPublish: () => {
      const base = metaBaseBudgetFromSimpleCampaignBudget(b);
      return (
        !!base &&
        typeof base.amount?.amount === "number" &&
        (base.type === "daily" || base.type === "lifetime")
      );
    },
    storedMetaScheduleGraphIds: () => [],
    fold: (arms) => arms.simple(b),
  };
}

/**
 * Base daily/lifetime slice used for Meta `daily_budget` / `lifetime_budget`
 * (and bid_strategy) for both simple and scheduled campaign budgets.
 */
export function getCampaignBudgetBaseForMeta(
  budget: MarketingCampaignBudget | undefined
): MarketingCampaignBudgetSimple | undefined {
  return campaignBudgetHandle(budget).baseForMeta();
}

export type CampaignBudgetDisplayMinorOptions = {
  /**
   * When set with a **daily** campaign budget, the returned minor total is
   * `displayMinorTotal() × scheduleInclusiveDayCount`. Ignored for `lifetime` and `scheduled`.
   */
  scheduleInclusiveDayCount?: number | null;
};

/**
 * Currency string for formatting a budget’s display minor (first segment currency for scheduled,
 * otherwise `amount.currency` on simple budgets).
 */
export function getCampaignBudgetDisplayCurrency(
  budget: MarketingCampaignBudget | MarketingCampaignBudgetSimple | undefined
): string | undefined {
  if (!budget) return undefined;
  if (budget.type === "scheduled") {
    return budget.segments?.find((s) => s.amount?.currency)?.amount?.currency;
  }
  return budget.amount?.currency;
}

/**
 * Minor-unit amount for UI labels: simple `amount.amount`, or **sum of all segment amounts**
 * for scheduled budgets (total planned spend over the campaign lifetime). This intentionally
 * differs from {@link getCampaignBudgetBaseForMeta}, which uses the minimum segment for Meta’s
 * base `daily_budget` / `lifetime_budget` wire format.
 *
 * For **`daily`** budgets only, {@link CampaignBudgetDisplayMinorOptions.scheduleInclusiveDayCount}
 * multiplies that per-day minor into an estimated run total (e.g. Algolia list “total budget”).
 */
export function getCampaignBudgetMinorAmountForDisplay(
  budget: MarketingCampaignBudget | undefined,
  options?: CampaignBudgetDisplayMinorOptions
): number | null {
  const baseMinor = campaignBudgetHandle(budget).displayMinorTotal();
  if (baseMinor == null) return null;
  const days = options?.scheduleInclusiveDayCount;
  if (
    budget?.type === "daily" &&
    typeof days === "number" &&
    Number.isFinite(days) &&
    days >= 1
  ) {
    return Math.round(baseMinor * days);
  }
  return baseMinor;
}

/**
 * Total planned budget in minor units for UI: campaign-level budget (including sum of
 * scheduled phases) when present and valid, otherwise sum of ad set budgets.
 */
export function getCampaignAggregatedBudgetAmountMinor(
  campaign: Pick<MarketingCampaign, "budget" | "adsets">
): number | null {
  if (campaign.budget) {
    const fromRoot = getCampaignBudgetMinorAmountForDisplay(campaign.budget);
    if (fromRoot != null) return fromRoot;
  }
  const adsets = campaign.adsets ?? [];
  let total = 0;
  for (const adset of adsets) {
    const a = coerceFiniteMinorAmount(adset.budget?.amount?.amount);
    if (a != null) total += a;
  }
  return total > 0 ? total : null;
}

export function hasCampaignBudgetForMetaPublish(
  budget: MarketingCampaignBudget | undefined
): budget is MarketingCampaignBudget {
  if (!budget) return false;
  return campaignBudgetHandle(budget).isCompleteForMetaPublish();
}

function parseIsoToUnixSeconds(iso: string): number | null {
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return null;
  return Math.floor(t / 1000);
}

/**
 * Inclusive count of **UTC calendar** days between two ISO instants (start/end of ad run).
 * Used with {@link getCampaignBudgetMinorAmountForDisplay} for **daily** budgets to estimate
 * total spend over the scheduled window. Returns `null` if bounds are missing/invalid or end
 * is before start.
 */
export function scheduleInclusiveUtcDayCountFromIso(
  scheduleStart?: string,
  scheduleEnd?: string
): number | null {
  if (!scheduleStart || !scheduleEnd) return null;
  const startMs = Date.parse(scheduleStart);
  const endMs = Date.parse(scheduleEnd);
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) return null;
  if (endMs < startMs) return null;
  const start = new Date(startMs);
  const end = new Date(endMs);
  const startUtc = Date.UTC(
    start.getUTCFullYear(),
    start.getUTCMonth(),
    start.getUTCDate()
  );
  const endUtc = Date.UTC(
    end.getUTCFullYear(),
    end.getUTCMonth(),
    end.getUTCDate()
  );
  const calendarDaySpan = Math.floor((endUtc - startUtc) / 86_400_000) + 1;
  return Math.max(1, calendarDaySpan);
}

/**
 * Validates phased budget segments (require ≥1 by default).
 */
export function validateBudgetSegments(params: {
  segments: MarketingCampaignBudgetSegment[];
  requireAtLeastOne: boolean;
  /** When true, each segment's `time_end` must equal the next segment's `time_start` (ISO equality). */
  requireContiguous?: boolean;
}): CampaignBudgetWindowsValidationError | null {
  const { segments, requireAtLeastOne, requireContiguous = true } = params;
  if (requireAtLeastOne && segments.length === 0) {
    return {
      code: "NO_SEGMENTS",
      message: "Scheduled campaign budget requires at least one phase.",
    };
  }
  if (segments.length > SCHEDULED_BUDGET_MAX_SEGMENTS) {
    return {
      code: "TOO_MANY_SEGMENTS",
      message: `At most ${SCHEDULED_BUDGET_MAX_SEGMENTS} budget phases are allowed.`,
    };
  }
  for (let i = 0; i < segments.length; i++) {
    const s = segments[i];
    const start = parseIsoToUnixSeconds(s.time_start);
    const end = parseIsoToUnixSeconds(s.time_end);
    if (start == null || end == null) {
      return {
        code: "INVALID_ISO_TIME",
        message: "Each phase must use valid ISO 8601 time_start and time_end.",
      };
    }
    if (end <= start) {
      return {
        code: "WINDOW_INVALID_RANGE",
        message: "Each phase must have time_end after time_start.",
      };
    }
    if (end - start < SCHEDULED_BUDGET_PHASE_MIN_DURATION_SECONDS) {
      return {
        code: "PHASE_TOO_SHORT",
        message: SCHEDULED_BUDGET_PHASE_MIN_DURATION_MESSAGE,
      };
    }
    const amt = s.amount?.amount;
    if (typeof amt !== "number" || !Number.isFinite(amt) || amt <= 0) {
      return {
        code: "WINDOW_INVALID_RANGE",
        message: "Each phase must have a positive amount.",
      };
    }
    if (requireContiguous && i < segments.length - 1) {
      const next = segments[i + 1];
      const endMs = Date.parse(s.time_end);
      const nextStartMs = Date.parse(next.time_start);
      if (
        !Number.isFinite(endMs) ||
        !Number.isFinite(nextStartMs) ||
        endMs !== nextStartMs
      ) {
        return {
          code: "TIMELINE_GAP",
          message:
            "Phases must be contiguous: each phase end must be the same instant as the next phase start.",
        };
      }
    }
  }
  return null;
}

/**
 * Each row’s `[time_start, time_end]` must lie inside `[flightStart, flightEnd]` **inclusive** on
 * the Unix timeline. Does not compare to `Date.now()` — a window may be as early as the flight
 * start (even if that instant is in the past).
 */
function budgetIsoRowsWithinFlightInclusive(params: {
  rows: Array<{ time_start: string; time_end: string }>;
  flightStart: number;
  flightEnd: number;
  invalidIsoMessage: string;
  beforeFlightMessage: string;
  afterFlightMessage: string;
}): CampaignBudgetWindowsValidationError | null {
  for (const row of params.rows) {
    const rowStart = parseIsoToUnixSeconds(row.time_start);
    const rowEnd = parseIsoToUnixSeconds(row.time_end);
    if (rowStart == null || rowEnd == null) {
      return {
        code: "INVALID_ISO_TIME",
        message: params.invalidIsoMessage,
      };
    }
    if (rowStart < params.flightStart) {
      return {
        code: "OUTSIDE_FLIGHT_START",
        message: params.beforeFlightMessage,
      };
    }
    if (rowEnd > params.flightEnd) {
      return {
        code: "OUTSIDE_FLIGHT_END",
        message: params.afterFlightMessage,
      };
    }
  }
  return null;
}

/**
 * Validates scheduled-budget windows (derived Meta rows before POST).
 *
 * When {@link validateCampaignBudgetWindowsParams.flightStartIso} and `flightEndIso` are both set,
 * each window must lie inside that ad run (inclusive). Past vs “now” is not checked.
 */
export type ValidateCampaignBudgetWindowsParams = {
  windows: MarketingCampaignBudgetScheduleWindow[];
  requireAtLeastOne: boolean;
  flightStartIso?: string | null;
  flightEndIso?: string | null;
};

export function validateCampaignBudgetWindows(
  params: ValidateCampaignBudgetWindowsParams
): CampaignBudgetWindowsValidationError | null {
  const { windows, requireAtLeastOne, flightStartIso, flightEndIso } = params;
  if (requireAtLeastOne && windows.length === 0) {
    return {
      code: "NO_WINDOWS",
      message: "Scheduled campaign budget requires at least one schedule row.",
    };
  }
  if (windows.length > SCHEDULED_BUDGET_MAX_SEGMENTS) {
    return {
      code: "TOO_MANY_WINDOWS",
      message: `At most ${SCHEDULED_BUDGET_MAX_SEGMENTS} budget schedule rows are allowed.`,
    };
  }
  for (const w of windows) {
    const start = parseIsoToUnixSeconds(w.time_start);
    const end = parseIsoToUnixSeconds(w.time_end);
    if (start == null || end == null) {
      return {
        code: "INVALID_ISO_TIME",
        message: "Each window must use valid ISO 8601 time_start and time_end.",
      };
    }
    if (end <= start) {
      return {
        code: "WINDOW_INVALID_RANGE",
        message: "Each window must have time_end after time_start.",
      };
    }
    if (end - start < SCHEDULED_BUDGET_PHASE_MIN_DURATION_SECONDS) {
      return {
        code: "PHASE_TOO_SHORT",
        message: SCHEDULED_BUDGET_PHASE_MIN_DURATION_MESSAGE,
      };
    }
    if (w.budget_value <= 0 || !Number.isFinite(w.budget_value)) {
      return {
        code: "WINDOW_INVALID_RANGE",
        message: "Each window must have a positive budget_value.",
      };
    }
  }

  const fs = flightStartIso?.trim();
  const fe = flightEndIso?.trim();
  if (fs || fe) {
    if (!fs || !fe) {
      return {
        code: "BOUNDARY_INVALID",
        message:
          "Both flight start and end are required to validate budget windows against the ad run schedule.",
      };
    }
    const flightStart = parseIsoToUnixSeconds(fs);
    const flightEnd = parseIsoToUnixSeconds(fe);
    if (flightStart == null || flightEnd == null) {
      return {
        code: "BOUNDARY_INVALID",
        message: "Run schedule must use valid ISO 8601 start and end times.",
      };
    }
    if (flightEnd <= flightStart) {
      return {
        code: "BOUNDARY_INVALID",
        message: "Run schedule must end after it starts.",
      };
    }
    const inFlightErr = budgetIsoRowsWithinFlightInclusive({
      rows: windows,
      flightStart,
      flightEnd,
      invalidIsoMessage:
        "Each window must use valid ISO 8601 time_start and time_end.",
      beforeFlightMessage:
        "Budget schedule windows must not start before the ad run schedule.",
      afterFlightMessage:
        "Budget schedule windows must not end after the ad run schedule.",
    });
    if (inFlightErr) return inFlightErr;
  }

  return null;
}

/**
 * Maps KYD windows to Meta `budget_schedule_specs` on campaign create/update.
 * Meta requires `budget_value_type` ∈ { ABSOLUTE, MULTIPLIER } on this field (not PERCENT).
 */
export function budgetScheduleWindowsToMetaSpecs(
  windows: MarketingCampaignBudgetScheduleWindow[]
): Array<{
  time_start: number;
  time_end: number;
  budget_value: number;
  budget_value_type: "ABSOLUTE" | "MULTIPLIER";
  recurrence_type: "ONE_TIME";
}> {
  const snapped: Array<{
    time_start: number;
    time_end: number;
    budget_value: number;
    budget_value_type: "ABSOLUTE" | "MULTIPLIER";
    recurrence_type: "ONE_TIME";
  }> = windows.map((w) => {
    const rawStart = parseIsoToUnixSeconds(w.time_start);
    const rawEnd = parseIsoToUnixSeconds(w.time_end);
    if (rawStart == null || rawEnd == null) {
      throw new Error("Invalid ISO time in budget schedule window");
    }
    const { time_start, time_end } = snapMetaBudgetScheduleWindowUnixRange({
      time_start: rawStart,
      time_end: rawEnd,
    });
    return {
      time_start,
      time_end,
      budget_value: w.budget_value,
      budget_value_type: w.budget_value_type,
      recurrence_type: "ONE_TIME" as const,
    };
  });
  return normalizeSnappedMetaBudgetScheduleWindowsNonOverlapping(snapped);
}

/** Form fields for POST `/{campaign-id}/budget_schedules` (Meta allows ABSOLUTE | MULTIPLIER, not PERCENT). */
export function budgetScheduleWindowToEdgeFields(w: {
  time_start: number;
  time_end: number;
  budget_value: number;
  budget_value_type: "ABSOLUTE" | "MULTIPLIER";
}): Record<string, string> {
  return {
    time_start: String(w.time_start),
    time_end: String(w.time_end),
    budget_value: String(w.budget_value),
    budget_value_type: w.budget_value_type,
  };
}

/** Snapped + normalized payload for POST `/{id}/budget_schedules` (includes stable input row index). */
export type MetaBudgetScheduleEdgePayload = {
  time_start: number;
  time_end: number;
  budget_value: number;
  budget_value_type: "ABSOLUTE" | "MULTIPLIER";
  /** Index into the `windows` array passed to {@link budgetScheduleWindowsToEdgePayloads}. */
  scheduleRowIndex: number;
};

/**
 * Meta does not accept budget schedule windows whose range is entirely before "now".
 * Lift starts to the current instant rounded up to Meta's next 15m boundary and, when the
 * whole window is past, use a minimal forward window from that boundary so POST payloads
 * stay valid.
 */
function clampBudgetScheduleWindowUnixRangeToNotBeforeNow(params: {
  rawStart: number;
  rawEnd: number;
  nowSec: number;
}): { rawStart: number; rawEnd: number } {
  const { nowSec } = params;
  const nowStartSec = snapUnixSecondsToMetaBudgetScheduleUtc(nowSec, "ceil");
  let { rawStart, rawEnd } = params;
  if (rawEnd <= nowSec) {
    rawStart = nowStartSec;
    rawEnd = nowStartSec + META_BUDGET_SCHEDULE_INTERVAL_SEC;
  } else if (rawStart < nowStartSec) {
    rawStart = nowStartSec;
  }
  return { rawStart, rawEnd };
}

export type BudgetScheduleWindowsToEdgePayloadsOptions = {
  /** Defaults to `Math.floor(Date.now() / 1000)`; set in tests for deterministic clamping. */
  nowUnixSeconds?: number;
};

export function budgetScheduleWindowsToEdgePayloads(
  windows: MarketingCampaignBudgetScheduleWindow[],
  options?: BudgetScheduleWindowsToEdgePayloadsOptions
): MetaBudgetScheduleEdgePayload[] {
  const nowSec =
    options?.nowUnixSeconds ?? Math.floor(Date.now() / 1000);
  const snapped = windows.map((w, scheduleRowIndex) => {
    const parsedStart = parseIsoToUnixSeconds(w.time_start);
    const parsedEnd = parseIsoToUnixSeconds(w.time_end);
    if (parsedStart == null || parsedEnd == null) {
      throw new Error("Invalid ISO time in budget schedule window");
    }
    const { rawStart, rawEnd } = clampBudgetScheduleWindowUnixRangeToNotBeforeNow({
      rawStart: parsedStart,
      rawEnd: parsedEnd,
      nowSec,
    });
    const { time_start, time_end } = snapMetaBudgetScheduleWindowUnixRange({
      time_start: rawStart,
      time_end: rawEnd,
    });
    return {
      time_start,
      time_end,
      budget_value: w.budget_value,
      budget_value_type: w.budget_value_type,
      scheduleRowIndex,
    };
  });
  return normalizeSnappedMetaBudgetScheduleWindowsNonOverlapping(snapped);
}

/**
 * Half-open Unix windows `[time_start, time_end)` intersect with positive width.
 * A shared boundary (`a.time_end === b.time_start`) is **not** an overlap.
 */
export function metaBudgetScheduleUnixWindowsOverlap(
  a: { time_start: number; time_end: number },
  b: { time_start: number; time_end: number }
): boolean {
  return a.time_start < b.time_end && b.time_start < a.time_end;
}

/**
 * Pairwise overlaps among proposed edge payloads. Should be empty after
 * {@link normalizeSnappedMetaBudgetScheduleWindowsNonOverlapping}; if not, mapping is inconsistent.
 */
export function findMetaBudgetSchedulePayloadPairOverlaps<
  T extends { time_start: number; time_end: number },
>(rows: T[]): Array<{ i: number; j: number }> {
  const out: Array<{ i: number; j: number }> = [];
  for (let i = 0; i < rows.length; i++) {
    const ai = rows[i];
    if (!ai) continue;
    for (let j = i + 1; j < rows.length; j++) {
      const aj = rows[j];
      if (!aj) continue;
      if (metaBudgetScheduleUnixWindowsOverlap(ai, aj)) {
        out.push({ i, j });
      }
    }
  }
  return out;
}

/**
 * Each proposed row that intersects an existing Meta `budget_schedules` row (same half-open rule).
 */
export function findMetaBudgetScheduleNewVsExistingOverlaps<
  T extends { time_start: number; time_end: number },
>(
  newRows: T[],
  existing: Array<{ time_start: number; time_end: number; id: string }>
): Array<{ newIndex: number; existingId: string }> {
  const out: Array<{ newIndex: number; existingId: string }> = [];
  for (let ni = 0; ni < newRows.length; ni++) {
    const n = newRows[ni];
    if (!n) continue;
    for (const ex of existing) {
      if (metaBudgetScheduleUnixWindowsOverlap(n, ex)) {
        out.push({ newIndex: ni, existingId: ex.id });
      }
    }
  }
  return out;
}

/**
 * Read minor-unit amount from guided-edit / API wire (flat number or `{ amount }` with **numeric** `amount` only).
 * Do not send `amount` as a string; persisted {@link Price} uses numbers after {@link normalizePrice}.
 */
export function readBudgetMinorAmount(
  value:
    | number
    | { amount?: number | null; currency?: string }
    | null
    | undefined
): number {
  if (value == null) return 0;
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "object" && value !== null && "amount" in value) {
    const a = (value as { amount?: unknown }).amount;
    if (typeof a === "number" && Number.isFinite(a)) return a;
  }
  return 0;
}

export function validateScheduledCampaignBudget(
  budget: Extract<MarketingCampaignBudget, { type: "scheduled" }>
): CampaignBudgetWindowsValidationError | null {
  return validateBudgetSegments({
    segments: budget.segments,
    requireAtLeastOne: true,
    requireContiguous: true,
  });
}

/**
 * Earliest ad-set start and latest ad-set end (UTC instants as ISO) across ad sets that
 * define both bounds. Used to ensure phased campaign budgets do not extend past the
 * combined delivery window.
 */
export function deriveCampaignFlightIsoBoundsFromAdsets(
  adsets: Array<{
    schedule?: { start_time?: string | null; stop_time?: string | null } | null;
  }>
): { flightStartIso: string; flightEndIso: string } | null {
  let minStart: number | null = null;
  let maxEnd: number | null = null;
  for (const a of adsets) {
    const s = a.schedule?.start_time?.trim();
    const e = a.schedule?.stop_time?.trim();
    if (!s || !e) continue;
    const ts = Date.parse(s);
    const te = Date.parse(e);
    if (!Number.isFinite(ts) || !Number.isFinite(te) || te <= ts) continue;
    minStart = minStart === null ? ts : Math.min(minStart, ts);
    maxEnd = maxEnd === null ? te : Math.max(maxEnd, te);
  }
  if (minStart === null || maxEnd === null) return null;
  return {
    flightStartIso: new Date(minStart).toISOString(),
    flightEndIso: new Date(maxEnd).toISOString(),
  };
}

/**
 * Ensures each phased budget segment lies fully inside `[flightStartIso, flightEndIso]`
 * (inclusive of endpoints), and that phased budgets **cover the whole run**: the first phase
 * starts exactly at the flight start and the last phase ends exactly at the flight end (no
 * budget-free gaps at the edges). Call after {@link validateBudgetSegments} /
 * {@link validateScheduledCampaignBudget}.
 */
export function validateScheduledBudgetWithinAdFlight(params: {
  segments: MarketingCampaignBudgetSegment[];
  flightStartIso: string | null | undefined;
  flightEndIso: string | null | undefined;
}): CampaignBudgetWindowsValidationError | null {
  const { segments, flightStartIso, flightEndIso } = params;
  if (segments.length === 0) return null;
  const fs = flightStartIso?.trim();
  const fe = flightEndIso?.trim();
  if (!fs || !fe) {
    return {
      code: "BOUNDARY_INVALID",
      message:
        "A start and end run schedule are required so scheduled budgets can be checked against the ad delivery window.",
    };
  }
  const flightStart = parseIsoToUnixSeconds(fs);
  const flightEnd = parseIsoToUnixSeconds(fe);
  if (flightStart == null || flightEnd == null) {
    return {
      code: "BOUNDARY_INVALID",
      message: "Run schedule must use valid ISO 8601 start and end times.",
    };
  }
  if (flightEnd <= flightStart) {
    return {
      code: "BOUNDARY_INVALID",
      message: "Run schedule must end after it starts.",
    };
  }
  const inFlightErr = budgetIsoRowsWithinFlightInclusive({
    rows: segments,
    flightStart,
    flightEnd,
    invalidIsoMessage:
      "Each budget phase must use valid ISO 8601 time_start and time_end.",
    beforeFlightMessage:
      "Scheduled budget phases must not start before the ad run schedule.",
    afterFlightMessage:
      "Scheduled budget phases must not end after the ad run schedule.",
  });
  if (inFlightErr) return inFlightErr;

  const first = segments[0];
  const last = segments[segments.length - 1];
  const firstStart = parseIsoToUnixSeconds(first.time_start);
  const lastEnd = parseIsoToUnixSeconds(last.time_end);
  if (firstStart != null && firstStart !== flightStart) {
    return {
      code: "FLIGHT_START_MISMATCH",
      message:
        "The first budget phase must start exactly when the campaign run starts.",
    };
  }
  if (lastEnd != null && lastEnd !== flightEnd) {
    return {
      code: "FLIGHT_END_MISMATCH",
      message:
        "The last budget phase must end exactly when the campaign run ends.",
    };
  }

  return null;
}

/** When no ad set contributes both schedule bounds for a combined delivery window. */
export const SCHEDULED_CAMPAIGN_ADSET_FLIGHT_REQUIRED_MESSAGE =
  "Scheduled budgets require each ad set to define both a start and end run time.";

/**
 * Validates a scheduled campaign budget against segment rules and the combined ad-set
 * delivery window (earliest start / latest end among ad sets with both schedule bounds).
 *
 * Call from the customer **ads-manager** API and `AdsManagerService.publishCampaign`
 * before starting publish Step Functions — keep publish Lambdas free of this duplicate check.
 */
export function validateScheduledCampaignForAdsManagerPublish(params: {
  budget: MarketingCampaignBudget | undefined;
  adsets: Array<{
    schedule?: { start_time?: string | null; stop_time?: string | null } | null;
  }>;
}): string | null {
  const { budget, adsets } = params;
  if (budget?.type !== "scheduled") return null;
  const winErr = validateScheduledCampaignBudget(budget);
  if (winErr) return winErr.message;
  const bounds = deriveCampaignFlightIsoBoundsFromAdsets(adsets ?? []);
  if (!bounds) return SCHEDULED_CAMPAIGN_ADSET_FLIGHT_REQUIRED_MESSAGE;
  const flightErr = validateScheduledBudgetWithinAdFlight({
    segments: budget.segments,
    flightStartIso: bounds.flightStartIso,
    flightEndIso: bounds.flightEndIso,
  });
  return flightErr?.message ?? null;
}

/** Meta Graph `budget_schedules` row (subset returned by GET). */
export type MetaBudgetScheduleRowBrief = {
  id: string;
  time_start: number;
  time_end: number;
  /**
   * Echoed from Graph when `GET /{campaign_id}` requests `budget_schedules{...,budget_value,...}`.
   * Use to verify Meta persisted the same increment (minor units, same as `daily_budget`) we POSTed.
   */
  budget_value?: number;
  budget_value_type?: string;
  /**
   * When set by the Meta publish flow, index into the `scheduleRows` list passed to
   * `budgetScheduleWindowsToEdgePayloads` so merge can attach Graph ids after sort/normalize
   * shifts unix bounds.
   */
  scheduleRowIndex?: number;
};

function metaBudgetScheduleSnappedKeyFromUnixBounds(params: {
  time_start: number;
  time_end: number;
}): string {
  const s = snapMetaBudgetScheduleWindowUnixRange(params);
  return `${s.time_start}\0${s.time_end}`;
}

function metaBudgetScheduleSnappedKeyFromIsoSegment(seg: {
  time_start: string;
  time_end: string;
}): string | null {
  const ts = Math.floor(Date.parse(seg.time_start) / 1000);
  const te = Math.floor(Date.parse(seg.time_end) / 1000);
  if (!Number.isFinite(ts) || !Number.isFinite(te)) return null;
  return metaBudgetScheduleSnappedKeyFromUnixBounds({
    time_start: ts,
    time_end: te,
  });
}

/**
 * Writes Meta `budget_schedules` Graph ids onto boosted {@link MarketingCampaignBudgetSegment}s
 * (same windows as derived `scheduleRows`, compared on Meta's 15m-snapped unix timeline).
 */
export function mergeMetaBudgetScheduleRowsIntoScheduledSegments(
  budget: MarketingCampaignBudgetScheduled,
  metaRows: MetaBudgetScheduleRowBrief[]
): MarketingCampaignBudgetSegment[] {
  const { scheduleRows } = phasedSegmentsToMetaBaseAndScheduleRows(budget);

  const idByScheduleRowIndex = new Map<number, string>();
  for (const row of metaRows) {
    if (typeof row.scheduleRowIndex === "number" && row.scheduleRowIndex >= 0) {
      idByScheduleRowIndex.set(row.scheduleRowIndex, row.id);
    }
  }

  const boostedKeys = new Set(
    scheduleRows
      .map((r) =>
        metaBudgetScheduleSnappedKeyFromIsoSegment({
          time_start: r.time_start,
          time_end: r.time_end,
        })
      )
      .filter((k): k is string => k != null)
  );

  const idBySnappedKey = new Map<string, string>();
  for (const row of metaRows) {
    const key = metaBudgetScheduleSnappedKeyFromUnixBounds({
      time_start: row.time_start,
      time_end: row.time_end,
    });
    idBySnappedKey.set(key, row.id);
  }

  return budget.segments.map((seg) => {
    const base: MarketingCampaignBudgetSegment = {
      time_start: seg.time_start,
      time_end: seg.time_end,
      amount: seg.amount,
    };

    const scheduleRowIdx = scheduleRows.findIndex(
      (r) => r.time_start === seg.time_start && r.time_end === seg.time_end
    );
    if (scheduleRowIdx >= 0) {
      const idFromIndex = idByScheduleRowIndex.get(scheduleRowIdx);
      if (idFromIndex) {
        return { ...base, external_id: idFromIndex };
      }
    }

    const key = metaBudgetScheduleSnappedKeyFromIsoSegment(seg);
    if (!key || !boostedKeys.has(key)) {
      return base;
    }
    const id = idBySnappedKey.get(key);
    return id ? { ...base, external_id: id } : base;
  });
}

/**
 * Parses `meta_budget_schedules` from a Meta campaign POST response (e.g. after posting
 * high-demand windows). Invalid shapes yield `[]` so callers can always pass the result to
 * {@link attachMetaBudgetScheduleExternalsToCampaign} (it no-ops on empty arrays).
 */
export function metaBudgetScheduleRowsFromCampaignGraphUpdate(
  result: unknown
): MetaBudgetScheduleRowBrief[] {
  if (result == null || typeof result !== "object") return [];
  const raw = (result as { meta_budget_schedules?: unknown })
    .meta_budget_schedules;
  return Array.isArray(raw) ? (raw as MetaBudgetScheduleRowBrief[]) : [];
}

export function attachMetaBudgetScheduleExternalsToCampaign(
  campaign: MarketingCampaign,
  metaRows: MetaBudgetScheduleRowBrief[]
): MarketingCampaign {
  if (campaign.budget?.type !== "scheduled" || metaRows.length === 0) {
    return campaign;
  }

  return {
    ...campaign,
    budget: {
      type: "scheduled",
      segments: mergeMetaBudgetScheduleRowsIntoScheduledSegments(
        campaign.budget,
        metaRows
      ),
    },
  };
}
